import React, { useMemo, useEffect } from 'react';
import { Outlet,  useLocation } from 'react-router-dom';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, Container, CssBaseline, Toolbar, useMediaQuery,BottomNavigation } from '@mui/material';


// project imports
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Customization from '../Customization';
import Header from './Header';
import HorizontalBar from './HorizontalBar';
import SectionTabs from './SectionTabs';
import Sidebar from './Sidebar';
import navigation from 'menu-items';
import { getSectionTabs } from 'menu-items/sectionTabs';
import { AdminMenu, isAdminPath } from 'Adminviews/menu';
import LAYOUT_CONST from 'constant';
import useConfig from 'hooks/useConfig';
import { drawerWidth } from 'store/constant';
import { openDrawer } from 'store/slices/menu';
import { useDispatch, useSelector } from 'store';
import Footer from './Footer/Footer';
import SubscriptionReminder from './SubscriptionReminder';

// assets
import { IconChevronRight } from '@tabler/icons';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open, layout }) => ({
    ...theme.typography.mainContent,
    ...(!open && {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter
        }),
        [theme.breakpoints.up('md')]: {
            marginLeft: layout === LAYOUT_CONST.VERTICAL_LAYOUT ? -(drawerWidth - 72) : '20px',
            width: `calc(100% - ${drawerWidth}px)`,
            marginTop: layout === 'horizontal' ? 135 : 88
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px',
            marginTop: 88
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px',
            marginRight: '10px',
            marginTop: 88
        }
    }),
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.shorter
        }),
        marginLeft: layout === 'horizontal' ? '20px' : 0,
        marginTop: layout === 'horizontal' ? 135 : 88,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        width: `calc(100% - ${drawerWidth}px)`,
        [theme.breakpoints.up('md')]: {
            marginTop: layout === 'horizontal' ? 135 : 88
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px',
            marginTop: 88
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px',
            marginTop: 88
        }
    })
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();
    const { pathname, search } = useLocation();
    // A member section page owns its own chrome — the `.phead` heading and tab
    // strip — so the admin strip stays off it. Two member routes are ALSO
    // listed as items in menu-items/Admin.js: /ResearchReport (the Research →
    // Reports tab) and /pratiksha (Satellite → Pratiksha), and because
    // isAdminPath is a pure path match they were drawing the admin strip above
    // their own tab strip, so the same page was announced twice.
    //
    // Suppressing it here rather than deleting those two entries from Admin.js
    // keeps them as jump links in the strip on genuine admin pages; only the
    // rendering on the member page changes.
    const showAdminMenu = isAdminPath(pathname) && !getSectionTabs(pathname, search);
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const { drawerOpen } = useSelector((state) => state.menu);
    const { container, drawerType, layout } = useConfig();

    useEffect(() => {
        if (drawerType === LAYOUT_CONST.DEFAULT_DRAWER) {
            dispatch(openDrawer(true));
        } else {
            dispatch(openDrawer(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawerType]);

    useEffect(() => {
        if (drawerType === LAYOUT_CONST.DEFAULT_DRAWER) {
            dispatch(openDrawer(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (matchDownMd) {
            dispatch(openDrawer(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchDownMd]);

    const condition = layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd;

    // Design's `.topbar`: 11px 22px padding. The design's 18px flex `gap` is
    // deliberately NOT set here — this app's Header children carry their own
    // right-margins (15/7/5/7/7px), so a gap would double-count and push this
    // nowrap, fixed-width bar past 1280px viewports.
    const header = useMemo(
        () => (
            <Toolbar sx={{ p: { xs: '11px 12px', sm: condition ? '10px' : '11px 22px' } }}>
                {/* 22px of side padding is a desktop measurement. Below sm it
                    comes in to 12px, which is the difference between the header
                    row fitting a phone and not. */}
                <Header />
            </Toolbar>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [layout, matchDownMd]
    );

    return (
        <Box>
        {/* Subscription expiry reminder dialog (shown on login per expiry rules) */}
        <SubscriptionReminder />
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            {/* header */}
            <AppBar
                enableColorOnDark
                position="fixed"
                color="inherit"
                elevation={0}
                sx={{
                    transition: drawerOpen ? theme.transitions.create('width') : 'none'
                }}
            >
                {header}
            </AppBar>

            {/* horizontal menu-list bar */}
            {layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd && <HorizontalBar />}

            {/* drawer */}
            {(layout === LAYOUT_CONST.VERTICAL_LAYOUT || matchDownMd) && <Sidebar />}

            {/* main content */}
            <Main theme={theme} open={drawerOpen} layout={layout}>
                <Container maxWidth={container ? 'lg' : false} {...(!container && { sx: { px: { xs: 0 } } })}>
                     {showAdminMenu && <AdminMenu />}
                    <Box sx={showAdminMenu ? { pt: 2 } : {}}>
                        <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
                        {/* Section tab strip — renders nothing outside the
                            section map, so pages without tabs are unaffected. */}
                        <SectionTabs />
                        <Outlet />
                    </Box>
                </Container>
            </Main>
            {/* <Customization /> */}
        </Box>
        <BottomNavigation sx={{ justifyContent: 'flex-end',position:'relative',right:'20px' }}>
       <Footer/>
    </BottomNavigation>
   
  
</Box>
    );
};

export default MainLayout;
